import React from 'react';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { formatCurrency } from '../../utils/helpers';

/**
 * Trend indicator with arrow and percentage
 */
export const TrendIndicator = ({ value, inverse = false }) => {
  if (value === null || value === undefined) return null;
  const isPositive = inverse ? value < 0 : value > 0;
  const color = isPositive ? 'text-green-600' : 'text-red-600';
  const Icon = isPositive ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center text-xs font-medium ${color}`}>
      <Icon size={14} />
      {Math.abs(value).toFixed(1)}%
    </span>
  );
};

/**
 * Simple progress bar with optional percentage display
 */
export const ProgressBar = ({ value, max, color = 'bg-primary-500', showPercent = true }) => {
  const percent = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div 
          className={`h-full ${color} rounded-full transition-all duration-500`}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>
      {showPercent && (
        <span className="text-xs text-gray-500 w-10 text-right">{percent.toFixed(0)}%</span>
      )}
    </div>
  );
};

/**
 * Simple vertical bar chart
 */
export const SimpleBarChart = ({ data, maxValue, color = 'bg-primary-500' }) => {
  const max = maxValue || Math.max(...data.map(d => d.value), 1);
  // Show roughly 12 labels at most so long ranges stay readable
  const labelEvery = Math.max(1, Math.ceil(data.length / 12));
  return (
    <div className="flex items-end gap-1 h-24">
      {data.map((item, i) => (
        <div key={i} className="flex-1 min-w-0 flex flex-col items-center justify-end h-full">
          <div
            className={`w-full max-w-[48px] ${color} rounded-t transition-all duration-300`}
            style={{ height: `${Math.round((item.value / max) * 72)}px`, minHeight: item.value > 0 ? '4px' : '0' }}
            title={`${item.label}: ${formatCurrency(item.value)}`}
          />
          <span className="text-[10px] text-gray-400 mt-1 h-3 w-full text-center overflow-hidden whitespace-nowrap">
            {i % labelEvery === 0 ? item.label : ''}
          </span>
        </div>
      ))}
    </div>
  );
};

/**
 * Collapsible section wrapper
 */
export const CollapsibleSection = ({ 
  title, 
  icon: Icon, 
  isExpanded, 
  onToggle, 
  count, 
  children,
  rightContent 
}) => {
  return (
    <div className="card p-0 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon size={16} className="text-gray-500" />}
          <span className="font-medium text-gray-900">{title}</span>
          {count !== undefined && (
            <span className="text-xs text-gray-400">({count})</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {rightContent}
          <svg 
            className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </button>
      
      {isExpanded && (
        <div className="border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

/**
 * Stat card for displaying key metrics
 */
export const StatCard = ({ 
  label, 
  value, 
  subtext, 
  icon: Icon, 
  color = 'gray',
  trend 
}) => {
  const colorClasses = {
    green: 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200',
    blue: 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200',
    purple: 'bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200',
    amber: 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200',
    red: 'bg-gradient-to-br from-red-50 to-rose-50 border-red-200',
    gray: 'bg-white border-gray-200',
  };
  
  const textColors = {
    green: 'text-green-600',
    blue: 'text-blue-600',
    purple: 'text-purple-600',
    amber: 'text-amber-600',
    red: 'text-red-600',
    gray: 'text-gray-600',
  };

  return (
    <div className={`card p-4 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className={`text-xs ${textColors[color]} font-medium`}>{label}</p>
          <p className={`text-2xl font-bold ${textColors[color].replace('600', '900')}`}>{value}</p>
          {subtext && (
            <div className="flex items-center gap-1 mt-1">
              <p className="text-xs text-gray-500">{subtext}</p>
              {trend !== undefined && <TrendIndicator value={trend} />}
            </div>
          )}
        </div>
        {Icon && <Icon className={textColors[color].replace('600', '500')} size={28} />}
      </div>
    </div>
  );
};